/**
 * 채팅 기능 관련 API 통신 모듈
 * 
 * ✅ 백엔드 Phase 2+3 통합 대응:
 * - 하이브리드 검색: BM25(키워드) + Vector(의미) 결합으로 정확도 20-30% 향상
 * - Redis 캐싱: 동일 쿼리 재검색 시 응답 속도 90% 향상
 * - MMR 다양성: 검색 결과 중복 제거 및 다양한 정보 제공
 * 
 * 이 모듈은 RAG 시스템의 채팅 기능과 관련된 모든 백엔드 API 통신을 처리합니다.
 * 일반 채팅, 스트리밍 채팅, 세션 관리 등 채팅 관련 모든 기능을 포함합니다.
 * 
 * 주요 기능:
 * - 일반/스트리밍 채팅 메시지 전송 (하이브리드 검색 자동 적용)
 * - 채팅 세션 생성 및 관리
 * - 채팅 히스토리 조회
 * - 실시간 응답 스트리밍 (긴 답변도 즉시 표시)
 */

import api from './api';

// 채팅 API 함수들
const chatApi = {
  /**
   * 일반 채팅 메시지 전송 (비스트리밍)
   * 
   * ✅ Phase 2+3: 백엔드에서 자동으로 하이브리드 검색 및 캐싱 적용
   * - BM25 키워드 검색 30% + Vector 의미 검색 70% 가중치
   * - 동일 쿼리는 Redis 캐시에서 즉시 반환 (응답 시간 ~10ms)
   * - MMR 알고리즘으로 중복 제거 및 다양성 확보
   * 
   * @param {Object} chatData - 채팅 요청 데이터
   * @param {Array} chatData.messages - 메시지 목록 [{ role: 'user', content: '질문' }]
   * @param {string} [chatData.kb_id] - 단일 지식베이스 ID
   * @param {Array} [chatData.kb_ids] - 여러 지식베이스 ID 목록 (다중 KB 검색)
   * @param {string} [chatData.model] - 사용할 LLM 모델 (기본: gpt-4o-mini)
   * @param {number} [chatData.temperature=0.7] - 생성 온도 (0.0=결정적, 1.0=창의적)
   * @param {boolean} [chatData.use_semantic_search=true] - 시맨틱 검색 사용 여부
   * @param {string} [chatData.session_id] - 채팅 세션 ID (대화 컨텍스트 유지)
   * @param {number} [chatData.top_k=30] - 검색할 최대 문서 수 (기본: 30)
   * @param {number} [chatData.min_score=0.3] - 최소 유사도 점수 (기본: 0.3)
   * 
   * @returns {Promise<Object>} 채팅 응답
   * @returns {string} return.answer - AI 생성 답변
   * @returns {Array} return.source_documents - 참조한 소스 문서 목록
   * @returns {number} return.search_time - 검색 소요 시간 (ms)
   * @returns {boolean} return.from_cache - 캐시에서 반환되었는지 여부
   * 
   * @example
   * const response = await chatApi.sendChatMessage({
   *   messages: [{ role: 'user', content: '회사 휴가 정책은?' }],
   *   kb_id: 'kb_company_manual',
   *   temperature: 0.5
   * });
   * console.log(response.answer);  // "연차는 15일입니다..."
   * console.log(response.source_documents);  // [{ chunk: "...", score: 0.85 }]
   */
  sendChatMessage: async (chatData) => {
    const response = await api.post('/chat', chatData);
    return response.data;
  },
  
  /**
   * 스트리밍 채팅 메시지 전송 (스트리밍 응답)
   * 
   * ✅ Phase 2+3: 하이브리드 검색 후 실시간 스트리밍 응답
   * - 검색은 하이브리드(BM25+Vector)로 진행
   * - LLM 응답은 실시간으로 청크 단위 전송
   * - 사용자 경험 향상: 긴 답변도 즉시 읽기 시작 가능
   * 
   * SSE(Server-Sent Events)와 유사한 방식으로 스트리밍 응답을 처리합니다.
   * 긴 응답을 실시간으로 화면에 표시하기 위해 사용됩니다.
   * 
   * @param {Object} chatData - 채팅 요청 데이터 (sendChatMessage와 동일)
   * @param {Function} onMessage - 새 메시지 청크를 수신할 때 호출할 콜백 함수
   *                                호출 형식: onMessage({ content: '답변 일부', done: false })
   * @param {Function} onError - 오류 발생 시 호출할 콜백 함수
   *                              호출 형식: onError(error)
   * @param {Function} onComplete - 스트림이 완료되었을 때 호출할 콜백 함수
   *                                 호출 형식: onComplete()
   * 
   * @returns {Object} 스트림 컨트롤러
   * @returns {Function} return.close - 스트림을 중단하는 함수
   * 
   * @example
   * const stream = await chatApi.sendStreamingChatMessage(
   *   {
   *     messages: [{ role: 'user', content: '긴 답변이 필요한 질문' }],
   *     kb_id: 'kb_manual'
   *   },
   *   (data) => {
   *     console.log('새 청크:', data.content);
   *     setAnswer(prev => prev + data.content);
   *   },
   *   (error) => console.error('에러:', error),
   *   () => console.log('완료!')
   * );
   * 
   * // 사용자가 중단 버튼 클릭 시
   * stream.close();
   */
  sendStreamingChatMessage: async (chatData, onMessage, onError, onComplete) => {
    try {
      // 요청에 타임스탬프를 추가하여 캐싱 방지
      const requestWithTimestamp = {
        ...chatData,
        timestamp: new Date().getTime() // 현재 시간을 밀리초로 추가
      };
      
      // fetch API를 사용하여 POST 요청 보내기
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify(requestWithTimestamp)
      });

      if (!response.ok) {
        throw new Error(`서버 오류: ${response.status} ${response.statusText}`);
      }

      // 응답을 읽을 수 있는 reader 생성
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      // EventSource와 유사하게 처리하기 위한 함수
      const processStream = async () => {
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            if (onComplete) onComplete();
            break;
          }
          
          // 새 데이터를 버퍼에 추가
          buffer += decoder.decode(value, { stream: true });
          
          // 버퍼에서 완전한 메시지들을 추출
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // 마지막 불완전한 메시지를 버퍼에 남김
          
          // 각 완전한 메시지 처리
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.substring(6); // 'data: ' 제거
                const data = JSON.parse(jsonStr);
                onMessage(data);
              } catch (e) {
                console.error('JSON 파싱 오류:', e);
              }
            }
          }
        }
      };
      
      // 스트림 처리 시작
      processStream().catch(error => {
        if (onError) onError(error);
      });
      
      // 스트림 종료를 위한 함수 반환
      return {
        close: () => {
          reader.cancel();
          if (onComplete) onComplete();
        }
      };
    } catch (error) {
      if (onError) onError(error);
      return {
        close: () => {}
      };
    }
  },
  
  /**
   * 채팅 세션 생성
   * 
   * 세션을 생성하면 대화 내용을 유지하고 나중에 다시 접근할 수 있습니다.
   * 
   * @param {Object} sessionData - 세션 생성 데이터
   * @param {string} sessionData.name - 세션 이름
   * @param {string} [sessionData.kb_id] - 연결할 지식베이스 ID
   * @returns {Promise<Object>} 생성된 세션 정보
   */
  createSession: async (sessionData) => {
    const response = await api.post('/chat/sessions', sessionData);
    return response.data;
  },
  
  /**
   * 채팅 세션 목록 조회
   * 
   * 사용자가 생성한 모든 채팅 세션 목록을 가져옵니다.
   * 
   * @returns {Promise<Object>} 세션 목록 데이터
   */
  listSessions: async () => {
    const response = await api.get('/chat/sessions');
    return response.data;
  },
  
  /**
   * 특정 세션의 채팅 기록 조회
   * 
   * @param {string} sessionId - 조회할 세션 ID
   * @param {number} [limit=100] - 가져올 최대 메시지 수
   * @param {number} [offset=0] - 페이지네이션 오프셋
   * @returns {Promise<Object>} 세션 채팅 기록
   */
  getSessionHistory: async (sessionId, limit = 100, offset = 0) => {
    const response = await api.get(`/chat/sessions/${sessionId}/history`, {
      params: { limit, offset }
    });
    return response.data;
  },
  
  /**
   * 채팅 세션 삭제
   * 
   * @param {string} sessionId - 삭제할 세션 ID
   * @returns {Promise<Object>} 삭제 결과 데이터
   */
  deleteSession: async (sessionId) => {
    const response = await api.delete(`/chat/sessions/${sessionId}`);
    return response.data;
  },

  // ── 방문자 세션 API (비로그인 사용자 채팅 이력 관리) ─────────────

  /** 방문자 채팅 세션 시작 */
  startVisitorSession: async (sessionId, visitorId, title = null) => {
    const response = await api.post('/visitor-sessions/start', {
      session_id: sessionId,
      visitor_id: visitorId,
      title,
    });
    return response.data;
  },

  /** 방문자 채팅 세션 종료 (탭 닫기 / X 버튼) */
  endVisitorSession: async (sessionId) => {
    // sendBeacon: 페이지 언로드 중에도 요청 보장
    // ⚠️ text/plain MIME 사용 → CORS preflight 없이 cross-origin 전송 가능
    //    백엔드 /end 엔드포인트는 raw body JSON 파싱 지원
    const baseUrl = process.env.REACT_APP_API_URL || '/api';
    const url = `${baseUrl}/visitor-sessions/end`;
    const body = JSON.stringify({ session_id: sessionId });
    if (navigator.sendBeacon) {
      // text/plain → CORS preflight 불필요 (simple request)
      navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
      return;
    }
    // sendBeacon 미지원 fallback (keepalive fetch)
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body,
        keepalive: true,
      });
    } catch (_) { /* 종료 중 실패는 무시 */ }
  },

  /** 방문자의 과거 채팅 세션 목록 조회 */
  listVisitorSessions: async (visitorId, limit = 30) => {
    const response = await api.get('/visitor-sessions', { params: { visitor_id: visitorId, limit } });
    return response.data;
  },

  /** 특정 세션의 메시지 이력 조회 (대화 복원) */
  getVisitorSessionMessages: async (sessionId) => {
    const response = await api.get(`/visitor-sessions/${sessionId}/messages`);
    return response.data;
  },

  /** 방문자 세션 삭제 */
  deleteVisitorSession: async (sessionId, visitorId) => {
    const response = await api.delete(`/visitor-sessions/${sessionId}`, {
      params: { visitor_id: visitorId },
    });
    return response.data;
  },
};

export default chatApi; 